// src/pages/TemplateListPage.tsx
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Menu,
  MenuItem,
  Tabs,
  Tab,
} from '@mui/material';
import { Edit, Delete, MoreVert, Visibility } from '@mui/icons-material';
import { format } from 'date-fns';
import {
  getTemplates,
  activateTemplate,
  deactivateTemplate,
  deleteTemplate,
  TemplateApiError,
  TemplateListItem,
} from '../services/templateApi';
import { useAuth } from '../contexts/AuthContext';
import { hasRole } from '../services/authApi';

type StatusFilter = 'ALL' | 'ACTIVE' | 'ARCHIVED';

export function TemplateListPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [templates, setTemplates] = useState<TemplateListItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('ALL');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<number | null>(null);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [menuTemplateId, setMenuTemplateId] = useState<number | null>(null);
  const [actionLoading, setActionLoading] = useState<number | null>(null);

  // Check if user is System Admin (for edit/delete/activate actions)
  const isAdmin = user ? hasRole(user, 'System Admin') : false;
  // @ts-ignore - intentionally unused
  const _isManager = user ? hasRole(user, 'Airport Manager') : false;

  // Load templates
  const loadTemplates = async (filterStatus?: StatusFilter) => {
    try {
      setIsLoading(true);
      setError(null);
      const status = filterStatus === 'ALL' ? undefined : filterStatus;
      const response = await getTemplates(status);
      setTemplates(response.templates);
    } catch (err) {
      const apiError = err as TemplateApiError;
      setError(apiError.message || 'Failed to load templates');
      console.error('Error loading templates:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadTemplates(statusFilter);
  }, [statusFilter]);

  const handleEditTemplate = (templateId: number) => {
    navigate(`/admin/templates/${templateId}/edit`);
    handleMenuClose();
  };

  const handleViewTemplate = (templateId: number) => {
    // For now, view navigates to edit page (T3.3 will implement this)
    navigate(`/admin/templates/${templateId}/edit`);
    handleMenuClose();
  };

  const handleDeleteClick = (templateId: number) => {
    setSelectedTemplate(templateId);
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  const handleDeleteConfirm = async () => {
    if (!selectedTemplate) return;
    try {
      setActionLoading(selectedTemplate);
      await deleteTemplate(selectedTemplate);
      setDeleteDialogOpen(false);
      setSelectedTemplate(null);
      // Reload templates
      loadTemplates(statusFilter);
    } catch (err) {
      const apiError = err as TemplateApiError;
      setError(apiError.message || 'Failed to delete template');
      console.error('Error deleting template:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleActivate = async (templateId: number) => {
    try {
      setActionLoading(templateId);
      await activateTemplate(templateId);
      loadTemplates(statusFilter);
    } catch (err) {
      const apiError = err as TemplateApiError;
      setError(apiError.message || 'Failed to activate template');
      console.error('Error activating template:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeactivate = async (templateId: number) => {
    try {
      setActionLoading(templateId);
      await deactivateTemplate(templateId);
      loadTemplates(statusFilter);
    } catch (err) {
      const apiError = err as TemplateApiError;
      setError(apiError.message || 'Failed to deactivate template');
      console.error('Error deactivating template:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, templateId: number) => {
    setMenuAnchor(event.currentTarget);
    setMenuTemplateId(templateId);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setMenuTemplateId(null);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ACTIVE':
        return 'success';
      case 'ARCHIVED':
        return 'default';
      case 'DRAFT':
        return 'warning';
      default:
        return 'default';
    }
  };

  const handleStatusFilterChange = (_event: React.SyntheticEvent, newValue: StatusFilter) => {
    setStatusFilter(newValue);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ color: 'white' }}>Template Management</Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Status Filter Tabs */}
      <Box sx={{ mb: 3 }}>
        <Tabs 
          value={statusFilter} 
          onChange={handleStatusFilterChange}
          sx={{
            '& .MuiTab-root': {
              color: 'white',
              '&.Mui-selected': {
                color: 'white',
              },
            },
            '& .MuiTabs-indicator': {
              backgroundColor: 'white',
            },
          }}
        >
          <Tab label="All Templates" value="ALL" />
          <Tab label="Active" value="ACTIVE" />
          <Tab label="Archived" value="ARCHIVED" />
        </Tabs>
      </Box>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : templates.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            No templates found.
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Created Date</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {templates.map((template) => (
                <TableRow key={template.templateId} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {template.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {template.description || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={template.status}
                      size="small"
                      color={getStatusColor(template.status) as any}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {format(new Date(template.createdAt), 'MMM dd, yyyy')}
                    </Typography>
                  </TableCell>
                  <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                    {actionLoading === template.templateId ? (
                      <CircularProgress size={20} />
                    ) : (
                      <>
                        {isAdmin && template.status === 'ARCHIVED' && (
                          <Button
                            size="small"
                            variant="outlined"
                            color="success"
                            onClick={() => handleActivate(template.templateId)}
                            sx={{ mr: 1 }}
                          >
                            Activate
                          </Button>
                        )}
                        {isAdmin && template.status === 'ACTIVE' && (
                          <Button
                            size="small"
                            variant="outlined"
                            color="warning"
                            onClick={() => handleDeactivate(template.templateId)}
                            sx={{ mr: 1 }}
                          >
                            Deactivate
                          </Button>
                        )}
                        <IconButton
                          size="small"
                          onClick={(e) => handleMenuOpen(e, template.templateId)}
                        >
                          <MoreVert />
                        </IconButton>
                        <Menu
                          anchorEl={menuAnchor}
                          open={Boolean(menuAnchor) && menuTemplateId === template.templateId}
                          onClose={handleMenuClose}
                        >
                          <MenuItem
                            onClick={() => {
                              handleViewTemplate(template.templateId);
                            }}
                          >
                            <Visibility sx={{ mr: 1 }} fontSize="small" />
                            View
                          </MenuItem>
                          {isAdmin && (
                            <MenuItem
                              onClick={() => {
                                handleEditTemplate(template.templateId);
                              }}
                            >
                              <Edit sx={{ mr: 1 }} fontSize="small" />
                              Edit
                            </MenuItem>
                          )}
                          {isAdmin && (
                            <MenuItem
                              onClick={() => {
                                handleDeleteClick(template.templateId);
                              }}
                              sx={{ color: 'error.main' }}
                            >
                              <Delete sx={{ mr: 1 }} fontSize="small" />
                              Delete
                            </MenuItem>
                          )}
                        </Menu>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Template</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this template? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={actionLoading !== null}
          >
            {actionLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

